import { useEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type { ActorRefFromLogic } from 'xstate';

import { localStorage } from '@change-corgi/core/storage';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { DraftClient, InMemoryAdapter, LocalStorageAdapter } from '#src/app/shared/api/petitionDraft';

import type { FlowActors, FlowChildren } from '../../actors/types';
import type { VARIANT_ACTOR_IDS } from '../../orchestrationLogic';

type VariantLogic = FlowActors[FlowChildren[(typeof VARIANT_ACTOR_IDS)[number]]];
type DraftOwner = ActorRefFromLogic<VariantLogic>;

/** Persists the active variant actor's draft, which is the draft's single owner. */
export function useSubscribeToMachine(actorRef: DraftOwner | undefined): void {
	const { errorReporter } = useUtilityContext();
	const queryClient = useQueryClient();
	const draftClient = useMemo(() => {
		return new DraftClient([new LocalStorageAdapter(localStorage), new InMemoryAdapter(queryClient)]);
	}, [queryClient]);

	// On initial mount, persist the draft to ensure we have it saved in our storage layer for future retrieval.
	useEffect(() => {
		const currentDraft = actorRef?.getSnapshot().context.draft;
		if (!currentDraft || !currentDraft.id || !currentDraft.authorId) return;
		const { authorId, ...petitionDraft } = currentDraft;

		void (async () => {
			try {
				await draftClient.updateDraft(authorId, petitionDraft);
			} catch (error) {
				void errorReporter.report(new Error('SAP: Could not sync draft', { cause: error }));
			}
		})();
	}, []);

	// Subscribe to future snapshot updates
	useEffect(() => {
		if (!actorRef) return undefined;
		const subscription = actorRef.subscribe((currentSnapshot) => {
			const { draft: currentDraft } = currentSnapshot.context;
			if (!currentDraft || !currentDraft.id || !currentDraft.authorId) return;
			const { authorId, ...petitionDraft } = currentDraft;

			// Fire-and-forget: intentionally not awaiting the promise as updates should not block UI.
			void (async () => {
				try {
					await draftClient.updateDraft(authorId, petitionDraft);
				} catch (error) {
					void errorReporter.report(new Error('SAP: Could not update draft', { cause: error }));
				}
			})();
		});
		return () => subscription.unsubscribe();
	}, [actorRef, errorReporter]);
}
